import { useEffect, useState, useCallback } from "react";
import { getLoadedShipment } from "inbounds/store/selectors/shipments";
import { getSellerId } from "common/user/UserSelectors";
import { initiateDownloadFromUrl } from "common/InitiateDownload";
import { toast } from "common/components/ui";
import { useIntl } from "react-intl";
import { useSPDispatch, useSPSelector } from "common/ReduxUtils";
import { fetchAppointmentDetails } from "inbounds/InboundActions";
import { selectAppointmentDetailsForShipment } from "inbounds/store/selectors/appointment/selectAppointmentDetails";

// Define the type based on the actual API response structure

interface AppointmentDetails {
  quantity: number | null;
  verifiedQuantity: number | null;
  proNumber: string | null;
  bolId: string | null;
  containerId: string | null;
  checkedOutAt: string | null;
  scheduledStartTime: string | null;
  loadFinishedAt: string | null;
  attachments: Array<{
    url: string;
    attachmentType: string | null;
  }>;
}

const defaultAppointmentData: AppointmentDetails = {
  quantity: null,
  verifiedQuantity: null,
  proNumber: null,
  bolId: null,
  containerId: null,
  checkedOutAt: null,
  scheduledStartTime: null,
  loadFinishedAt: null,
  attachments: [],
};

export const useAppointmentDetailsBase = (
  shipmentId: number | null,
  sellerId: string | null
): AppointmentDetails & {
  isLoading: boolean;
  error: string | null;
  onDownloadBol: () => Promise<void>;
  isDownloadLoading: boolean;
} => {
  const dispatch = useSPDispatch();
  const { formatMessage } = useIntl();
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);

  // Select data from Redux store
  const appointmentDetailsState = useSPSelector(selectAppointmentDetailsForShipment(shipmentId));

  const appointmentData = appointmentDetailsState?.data ?? defaultAppointmentData;
  const isLoading = appointmentDetailsState?.isLoading ?? false;
  const error = appointmentDetailsState?.error ?? null;

  useEffect(() => {
    // Fetch data if shipmentId is valid and data isn't already loaded/loading
    if (shipmentId && !appointmentDetailsState) {
      dispatch(fetchAppointmentDetails(shipmentId));
    }
    // Consider adding logic here to refetch if data is stale or if shipmentId changes
    // but the current logic fetches only if data is completely missing for the ID.
  }, [dispatch, shipmentId, appointmentDetailsState]); // Depend on state to prevent re-fetch if data arrives

  const onDownloadBol = useCallback(async () => {
    const bolAttachments = appointmentData.attachments ?? [];

    if (!bolAttachments.length) {
      toast.error(
        formatMessage({
          id: "storage.inbounds.detail.downloadBolError",
          defaultMessage: "No BOL attachments available for download",
        })
      );
      return;
    }

    setIsDownloadLoading(true);
    try {
      await Promise.all(
        bolAttachments.map(async (attachment, index) => {
          if (attachment.url) {
            await initiateDownloadFromUrl(attachment.url, `bol-attachment-${index + 1}.pdf`);
          }
        })
      );
    } catch (downloadError) {
      toast.error(
        formatMessage({
          id: "storage.inbounds.detail.downloadBolError",
          defaultMessage: "Failed to download BOL",
        })
      );
    } finally {
      setIsDownloadLoading(false);
    }
  }, [appointmentData.attachments, formatMessage]);

  return {
    ...appointmentData,
    isLoading,
    error,
    onDownloadBol,
    isDownloadLoading,
  };
};

// Regular inbound shipment hook
export const useAppointmentDetails = (): AppointmentDetails & {
  isLoading: boolean;
  error: string | null;
  onDownloadBol: () => Promise<void>;
  isDownloadLoading: boolean;
} => {
  const loadedShipment = useSPSelector(getLoadedShipment);
  const shipmentId = loadedShipment?.id ?? null;
  const sellerId = useSPSelector(getSellerId);

  return useAppointmentDetailsBase(shipmentId, sellerId);
};
